import * as React from 'react';
import { addDays, startOfMonth } from '@cfl/shared';
import { orgToday } from '@/lib/today';
import { FileText, Printer } from 'lucide-react';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, CardHeader, CardTitle,
  Combobox, Dialog, DialogContent, DialogHeader, DialogTitle, Input, Label, Select,
  SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton, Table, TableBody,
  TableCell, TableHead, TableHeader, TableRow, Textarea,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { ReceiptArt } from '@/components/illustrations';
import { ApiError, toastFailure } from '@/lib/api';
import { useCustomers } from '@/lib/catalog-api';
import {
  hours, money, useInvoice, useInvoicePreview, useInvoices, useRaiseInvoice,
  useSetInvoiceStatus, type InvoiceGrouping, type InvoiceStatus,
} from '@/lib/invoice-api';

/**
 * Invoices.
 *
 * The previous system had this as a template engine with five document formats
 * and fifteen ways to group the lines, and raised zero invoices in the eighteen
 * months it ran. So this is the workflow rather than the framework: pick a
 * customer and a period, look at what would be billed, raise it, print it.
 *
 * The document is a page with print styles, not a generated PDF. The browser's
 * own "save as PDF" produces the file, which means no rendering dependency and a
 * document anybody can check on screen before it becomes one.
 */
const GROUPINGS: Array<{ value: InvoiceGrouping; label: string }> = [
  { value: 'activity', label: 'One line per project and activity' },
  { value: 'project', label: 'One line per project' },
  { value: 'person', label: 'One line per person' },
  { value: 'entry', label: 'One line per entry' },
  { value: 'total', label: 'A single line' },
];

const STATUS_STYLE: Record<InvoiceStatus, string> = {
  NEW: 'bg-muted text-foreground',
  PENDING: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
  PAID: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200',
  CANCELED: 'bg-muted text-muted-foreground line-through',
};

export function InvoicesPage() {
  const { data: customers } = useCustomers();
  const { data: invoices, isPending } = useInvoices();
  const preview = useInvoicePreview();
  const raise = useRaiseInvoice();
  const setStatus = useSetInvoiceStatus();

  const [customerId, setCustomerId] = React.useState('');
  const [from, setFrom] = React.useState(() => firstOfLastMonth());
  const [to, setTo] = React.useState(() => lastOfLastMonth());
  const [grouping, setGrouping] = React.useState<InvoiceGrouping>('activity');
  const [comment, setComment] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [showing, setShowing] = React.useState<string | null>(null);

  const options = (customers ?? []).map((c) => ({
    value: c.id,
    label: c.name,
    description: c.number ? `#${c.number}` : undefined,
  }));

  const ask = () => {
    setError(null);
    if (!customerId) return setError('Pick a customer first.');
    if (to < from) return setError('The end date is before the start date.');
    preview.mutate(
      { customerId, from, to, grouping },
      { onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not work that out.') },
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Invoices</h1>
        <p className="text-sm text-muted-foreground">
          Billable time that has not been invoiced yet, gathered by customer and period.
          Raising an invoice marks its entries as billed, so they cannot be edited or
          billed again.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Raise an invoice</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label>Customer</Label>
              <div className="w-64">
                <Combobox
                  value={customerId}
                  onChange={(v) => { setCustomerId(v); preview.reset(); }}
                  options={options}
                  placeholder="Pick a customer"
                  searchPlaceholder="Search customers"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-from">From</Label>
              <Input id="inv-from" type="date" className="w-40" value={from}
                     onChange={(e) => { setFrom(e.target.value); preview.reset(); }} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-to">To</Label>
              <Input id="inv-to" type="date" className="w-40" value={to}
                     onChange={(e) => { setTo(e.target.value); preview.reset(); }} />
            </div>
            <div className="space-y-1.5">
              <Label>Lines</Label>
              <Select
                value={grouping}
                onValueChange={(v) => { setGrouping(v as InvoiceGrouping); preview.reset(); }}
              >
                <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GROUPINGS.map((g) => (
                    <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button hint={"Preview what would be invoiced — nothing is saved"} onClick={ask} disabled={preview.isPending}>
              {preview.isPending ? 'Working it out…' : 'Show me what would be billed'}
            </Button>
          </div>

          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}

          {preview.data && (
            <div className="space-y-4 rounded-md border p-4">
              {preview.data.entryCount === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nothing to bill for {preview.data.customer.name} between {from} and {to}.
                  Work that is already invoiced, marked unbillable, or belongs to another
                  customer is not counted.
                </p>
              ) : (
                <>
                  {/* Said plainly, because it is the likely outcome here: no rate was
                      ever configured in the old system, so migrated work prices at
                      nothing until somebody sets one. */}
                  {preview.data.unpricedCount > 0 && (
                    <Alert className="border-amber-500/40">
                      <AlertDescription className="text-sm">
                        {preview.data.unpricedCount} of {preview.data.entryCount} entries have
                        no rate and would bill nothing. Set a rate on the customer, project or
                        activity, then look again.
                      </AlertDescription>
                    </Alert>
                  )}
                  <LineTable
                    lines={preview.data.lines}
                    currency={preview.data.currency}
                    net={preview.data.netMinor}
                    tax={preview.data.taxMinor}
                    gross={preview.data.grossMinor}
                    taxBasisPoints={preview.data.taxBasisPoints}
                    seconds={preview.data.billedSeconds}
                  />
                  <div className="space-y-1.5">
                    <Label htmlFor="inv-comment">Note on the invoice</Label>
                    <Textarea
                      id="inv-comment"
                      rows={2}
                      value={comment}
                      placeholder="Optional. Appears on the document."
                      onChange={(e) => setComment(e.target.value)}
                    />
                  </div>
                  <Button hint={"Create an invoice from the unbilled time"}
                    disabled={raise.isPending || preview.data.netMinor === '0'}
                    onClick={() => {
                      setError(null);
                      raise.mutate(
                        { customerId, from, to, grouping, comment: comment || null },
                        {
                          onSuccess: (created) => { setComment(''); preview.reset(); setShowing(created.id); },
                          onError: (e) =>
                            setError(e instanceof ApiError ? e.message : 'Could not raise it.'),
                        },
                      );
                    }}
                  >
                    {raise.isPending ? 'Raising…' : `Raise invoice for ${money(preview.data.grossMinor, preview.data.currency)}`}
                  </Button>
                  {preview.data.netMinor === '0' && <span className="text-xs text-muted-foreground">Nothing unbilled in this range to put on an invoice</span>}
                  {preview.data.netMinor === '0' && (
                    <p className="text-xs text-muted-foreground">Nothing to bill: every line prices at 0, so there is no invoice to raise.</p>
                  )}
                  {/* Repeated beside the button: the one above the preview is out of
                      sight by the time somebody has scrolled down to press it. */}
                  {error && <p role="alert" className="text-sm font-medium text-destructive">{error}</p>}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Raised</CardTitle>
        </CardHeader>
        <CardContent>
          {isPending && <Skeleton className="h-24 w-full" />}
          {invoices && invoices.length === 0 && (
            <EmptyState art={ReceiptArt} title="No invoice has been raised yet" body="Invoices raised from approved time show up here, with what each one billed." />
          )}
          {invoices && invoices.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">{inv.number}</TableCell>
                    <TableCell>{inv.customerName}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {inv.from} to {inv.to}
                    </TableCell>
                    <TableCell className="ts-num text-right">{hours(inv.billedSeconds)}</TableCell>
                    <TableCell className="ts-num text-right">
                      {money(inv.grossMinor, inv.currency)}
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_STYLE[inv.status]} variant="secondary">
                        {inv.status.toLowerCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button hint={"Open this invoice"} size="sm" variant="outline" onClick={() => setShowing(inv.id)}>
                          <FileText className="mr-1 size-3.5" /> Open
                        </Button>
                        {/* Only the moves the rules allow are offered. Cancelling
                            releases the hours, which is worth saying on the button's
                            own screen rather than after the fact. */}
                        {inv.status === 'NEW' && (
                          <Button hint={"Record that the invoice has been sent"} size="sm" variant="outline" disabled={setStatus.isPending}
                                  onClick={() => setStatus.mutate({ id: inv.id, status: 'PENDING' }, { onError: toastFailure(`Could not mark ${inv.number} sent`) })}>
                            Mark sent
                          </Button>
                        )}
                        {(inv.status === 'NEW' || inv.status === 'PENDING') && (
                          <Button hint={"Record that the invoice has been paid"} size="sm" variant="outline" disabled={setStatus.isPending}
                                  onClick={() => setStatus.mutate({ id: inv.id, status: 'PAID' }, { onError: toastFailure(`Could not mark ${inv.number} paid`) })}>
                            Mark paid
                          </Button>
                        )}
                        {inv.status !== 'CANCELED' && (
                          <Button hint={"Cancel this invoice — its entries become billable again"} size="sm" variant="ghost" disabled={setStatus.isPending}
                                  onClick={() => {
                                    if (window.confirm(
                                      `Cancel ${inv.number}? Its ${inv.entryCount ?? 0} entries go back to being billable, and the number is not reused.`,
                                    )) setStatus.mutate({ id: inv.id, status: 'CANCELED' }, { onError: toastFailure(`Could not cancel ${inv.number}`) });
                                  }}>
                            Cancel
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <InvoiceDocument id={showing} onClose={() => setShowing(null)} />
    </div>
  );
}

/** The lines plus the three totals, shared by the preview and the document. */
function LineTable({
  lines, currency, net, tax, gross, taxBasisPoints, seconds,
}: {
  lines: Array<{ label: string; seconds: number; amountMinor: string; from: string; to: string }>;
  currency: string;
  net: string;
  tax: string;
  gross: string;
  taxBasisPoints: number;
  seconds: number;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Description</TableHead>
          <TableHead className="text-right">Hours</TableHead>
          <TableHead className="text-right">Amount</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {lines.map((line, i) => (
          <TableRow key={`${line.label}-${i}`}>
            <TableCell>
              {line.label}
              {line.from !== line.to && (
                <span className="block text-xs text-muted-foreground">
                  {line.from} to {line.to}
                </span>
              )}
            </TableCell>
            <TableCell className="ts-num text-right">{hours(line.seconds)}</TableCell>
            <TableCell className="ts-num text-right">{money(line.amountMinor, currency)}</TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell className="font-medium">Net</TableCell>
          <TableCell className="ts-num text-right font-medium">{hours(seconds)}</TableCell>
          <TableCell className="ts-num text-right font-medium">{money(net, currency)}</TableCell>
        </TableRow>
        {taxBasisPoints > 0 && (
          <TableRow>
            <TableCell colSpan={2}>Tax at {(taxBasisPoints / 100).toFixed(2)}%</TableCell>
            <TableCell className="ts-num text-right">{money(tax, currency)}</TableCell>
          </TableRow>
        )}
        <TableRow>
          <TableCell colSpan={2} className="text-base font-semibold">Total</TableCell>
          <TableCell className="ts-num text-right text-base font-semibold">
            {money(gross, currency)}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

/**
 * The invoice as a document.
 *
 * `window.print()` on a dialog whose print styles hide everything else. No PDF
 * library: the browser already has one, and a document that is readable on screen
 * before it is printed is a document somebody can actually check.
 */
function InvoiceDocument({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data, isPending } = useInvoice(id);

  return (
    <Dialog open={id !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto print:max-h-none print:max-w-none print:overflow-visible">
        <DialogHeader>
          <DialogTitle>{data ? `Invoice ${data.number}` : 'Invoice'}</DialogTitle>
        </DialogHeader>
        {isPending && <Skeleton className="h-64 w-full" />}
        {data && (
          <div id="invoice-print" className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="text-sm">
                <p className="text-lg font-semibold">{data.organizationName}</p>
                {/* Each line only when it has something in it: a printed invoice
                    showing an empty "GSTIN" reads as a system fault, not as a
                    field somebody has not filled in yet. */}
                {data.issuer.address && (
                  <p className="whitespace-pre-wrap text-muted-foreground">{data.issuer.address}</p>
                )}
                {data.issuer.taxNumber && (
                  <p className="text-muted-foreground">GSTIN {data.issuer.taxNumber}</p>
                )}
                <p className="mt-2 font-medium">Invoice {data.number}</p>
              </div>
              <div className="text-sm">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Billed to
                </p>
                <p className="font-medium">{data.customer.name}</p>
                {data.customer.number && (
                  <p className="text-muted-foreground">#{data.customer.number}</p>
                )}
                {data.customer.country && (
                  <p className="text-muted-foreground">{data.customer.country}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
              <Figure label="Issued" value={data.issuedOn} />
              <Figure label="Due" value={data.dueOn} />
              <Figure label="Period" value={`${data.from} to ${data.to}`} />
              <Figure label="Status" value={data.status.toLowerCase()} />
            </div>

            <LineTable
              lines={data.lines}
              currency={data.currency}
              net={data.netMinor}
              tax={data.taxMinor}
              gross={data.grossMinor}
              taxBasisPoints={data.taxBasisPoints}
              seconds={data.billedSeconds}
            />

            {data.comment && <p className="whitespace-pre-wrap text-sm">{data.comment}</p>}

            {/* How to pay it. Below the total, because that is the order somebody
                reads an invoice in - what is owed, then where to send it. */}
            {(data.issuer.paymentTerms || data.issuer.bankDetails) && (
              <div className="grid gap-4 border-t pt-4 text-sm sm:grid-cols-2">
                {data.issuer.paymentTerms && (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Terms of payment
                    </p>
                    <p className="whitespace-pre-wrap">{data.issuer.paymentTerms}</p>
                  </div>
                )}
                {data.issuer.bankDetails && (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Bank account
                    </p>
                    <p className="whitespace-pre-wrap">{data.issuer.bankDetails}</p>
                  </div>
                )}
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Raised by {data.raisedBy} on {data.createdAt.slice(0, 10)}
              {data.paidOn ? `, paid ${data.paidOn}` : ''}. Covers {data.entryCount ?? 0} time
              entries, which are locked against editing while this invoice stands.
            </p>

            <div className="flex justify-end gap-2 print:hidden">
              <Button hint={"Print it, or choose Save as PDF in the print dialog"} variant="outline" onClick={() => window.print()}>
                <Printer className="mr-1 size-4" /> Print or save as PDF
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="font-medium">{value}</p>
    </div>
  );
}

/** Last month, because that is the period somebody almost always wants. */
function firstOfLastMonth(): string {
  return startOfMonth(lastOfLastMonth());
}

function lastOfLastMonth(): string {
  return addDays(startOfMonth(orgToday()), -1);
}
